import { toast } from 'sonner'
import { Loader2, CheckCircle2, XCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { router } from '@/app/Router'
import {
  useBackgroundTasksStore,
  type BackgroundTask,
} from '@/stores/background-tasks-store'

function navigateTo(path: string) {
  router.navigate(path)
}

function TaskIcon({ status }: { status: BackgroundTask['status'] }) {
  switch (status) {
    case 'processing':
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
    case 'completed':
      return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
    case 'failed':
      return <XCircle className="h-3.5 w-3.5 text-red-500" />
  }
}

function TaskItem({ task }: { task: BackgroundTask }) {
  const dismissTask = useBackgroundTasksStore((s) => s.dismissTask)

  const handleClick = () => {
    if (task.status === 'completed' && task.result?.navigateTo) {
      navigateTo(task.result.navigateTo)
      dismissTask(task.id)
    } else if (task.status === 'failed' && task.error) {
      toast.error('Background task failed', { description: task.error })
    }
  }

  const isClickable =
    (task.status === 'completed' && task.result?.navigateTo) ||
    (task.status === 'failed' && task.error)

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-3 py-2 rounded-md transition-colors',
        isClickable && 'cursor-pointer hover:bg-muted/60'
      )}
      onClick={isClickable ? handleClick : undefined}
    >
      <TaskIcon status={task.status} />
      <span className="text-sm flex-1 min-w-0 truncate">
        {task.status === 'completed' && task.result?.message
          ? task.result.message
          : task.status === 'failed'
            ? task.error || 'Failed'
            : task.label}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation()
          dismissTask(task.id)
        }}
        className="flex-shrink-0 p-0.5 rounded hover:bg-muted text-muted-foreground/60 hover:text-muted-foreground transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}

export function BackgroundTasksIndicator() {
  const tasks = useBackgroundTasksStore((s) => s.tasks)
  const dismissTask = useBackgroundTasksStore((s) => s.dismissTask)

  if (tasks.length === 0) return null

  const processingCount = tasks.filter((t) => t.status === 'processing').length
  const hasCompleted = tasks.some((t) => t.status === 'completed')
  const hasFailed = tasks.some((t) => t.status === 'failed')

  // Single task — render as a clickable pill directly
  if (tasks.length === 1) {
    const task = tasks[0]
    const isClickable =
      (task.status === 'completed' && task.result?.navigateTo) ||
      (task.status === 'failed' && task.error)

    const handleClick = () => {
      if (task.status === 'completed' && task.result?.navigateTo) {
        navigateTo(task.result.navigateTo)
        dismissTask(task.id)
      } else if (task.status === 'failed' && task.error) {
        toast.error('Background task failed', { description: task.error })
        dismissTask(task.id)
      }
    }

    return (
      <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div
          className={cn(
            'flex items-center gap-2.5 rounded-full px-4 py-2.5 shadow-lg border',
            'bg-card text-card-foreground',
            isClickable && 'cursor-pointer hover:shadow-xl transition-shadow',
            task.status === 'completed' && 'border-emerald-200 dark:border-emerald-800',
            task.status === 'failed' && 'border-red-200 dark:border-red-800',
            task.status === 'processing' && 'border-primary/20 dark:border-primary'
          )}
          onClick={isClickable ? handleClick : undefined}
        >
          <TaskIcon status={task.status} />
          <span className="text-sm font-medium max-w-[240px] truncate">
            {task.status === 'completed' && task.result?.message
              ? task.result.message
              : task.status === 'failed'
                ? task.error || 'Failed'
                : task.label}
          </span>
          {isClickable && task.status === 'completed' && (
            <span className="text-xs text-muted-foreground">Click to view</span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation()
              dismissTask(task.id)
            }}
            className="ml-1 p-0.5 rounded-full hover:bg-muted text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    )
  }

  // Multiple tasks — pill with count badge + popover
  const pillLabel = processingCount > 0
    ? `${processingCount} processing...`
    : hasCompleted
      ? 'Tasks complete'
      : 'Tasks failed'

  const pillBorderClass = processingCount > 0
    ? 'border-primary/20 dark:border-primary'
    : hasCompleted
      ? 'border-emerald-200 dark:border-emerald-800'
      : 'border-red-200 dark:border-red-800'

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'rounded-full px-4 py-2.5 h-auto shadow-lg gap-2.5',
              'bg-card hover:bg-card hover:shadow-xl transition-shadow',
              pillBorderClass
            )}
          >
            {processingCount > 0 ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            ) : hasFailed ? (
              <XCircle className="h-3.5 w-3.5 text-red-500" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            )}
            <span className="text-sm font-medium">{pillLabel}</span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {tasks.length}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          className="w-80 p-2"
          sideOffset={8}
        >
          <div className="space-y-0.5">
            {tasks.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
